import { API_BASE, messageFromErrorBody } from './authApi';
import { authHeaders } from './authStorage';

const jsonHeaders = () => ({
  'Content-Type': 'application/json',
  Accept: 'application/json',
  ...authHeaders(),
});

/** Map API DTO to BranchesPage row shape */
export function mapBranchFromApi(d) {
  const contracts = Array.isArray(d.contracts) ? d.contracts : [];
  return {
    id: d.id,
    name: d.name ?? '',
    crn: d.commercialRegistrationNumber ?? '',
    address: d.address ?? '',
    phone: d.phone ?? '',
    email: d.email ?? '',
    registeredCapital: d.registeredCapital ?? '',
    paidInCapital: d.paidInCapital ?? '',
    authorizedCapital: d.authorizedCapital ?? '',
    sharesCount: d.sharesCount ?? '',
    shareParValue: d.shareParValue ?? '',
    notes: d.notes ?? '',
    status: d.isActive ? 'active' : 'inactive',
    isMain: d.isMain ?? false,
    contracts: contracts.map((c) => ({
      id: c.id,
      name: c.name,
      type: c.type ?? 'pdf',
      size: c.size ?? '—',
      ...(c.storedFileName ? { storedFileName: c.storedFileName } : {}),
    })),
  };
}

/** Build PUT body from form state (excludes UI-only fields). */
export function mapFormToUpdatePayload(form) {
  const { newAttName, newAttType, contracts, ...rest } = form;
  return {
    name: rest.name?.trim() ?? '',
    commercialRegistrationNumber: rest.crn?.trim() || null,
    address: rest.address?.trim() || null,
    phone: rest.phone?.trim() || null,
    email: rest.email?.trim() || null,
    registeredCapital: rest.registeredCapital === '' ? null : Number(rest.registeredCapital),
    paidInCapital: rest.paidInCapital === '' ? null : Number(rest.paidInCapital),
    authorizedCapital: rest.authorizedCapital === '' ? null : Number(rest.authorizedCapital),
    sharesCount: rest.sharesCount === '' ? null : Number(rest.sharesCount),
    shareParValue: rest.shareParValue === '' ? null : Number(rest.shareParValue),
    notes: rest.notes?.trim() || null,
    isActive: rest.status === 'active',
    isMain: !!rest.isMain,
    contracts: (contracts || [])
      .filter((c) => !c?.__file)
      .map(({ id, name, type, size, storedFileName }) => ({
        id: id || `c-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        name,
        type: type || 'pdf',
        size: size || '—',
        ...(storedFileName ? { storedFileName } : {}),
      })),
  };
}

export async function fetchBranchById(id) {
  const res = await fetch(`${API_BASE}/api/branches/${id}/detail`, {
    headers: { Accept: 'application/json', ...authHeaders() },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(messageFromErrorBody(data, 'تعذر تحميل بيانات الفرع'));
  }
  return mapBranchFromApi(data);
}

export async function fetchBranches() {
  const res = await fetch(`${API_BASE}/api/branches`, { headers: { Accept: 'application/json', ...authHeaders() } });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(messageFromErrorBody(data, 'تعذر تحميل الفروع'));
  }
  const list = Array.isArray(data) ? data : [];
  return list.map(mapBranchFromApi);
}

export async function createBranch(payload) {
  const res = await fetch(`${API_BASE}/api/branches`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(messageFromErrorBody(data, 'تعذر إضافة الفرع'));
  }
  return mapBranchFromApi(data);
}

export async function updateBranch(id, payload) {
  const res = await fetch(`${API_BASE}/api/branches/${id}/edit`, {
    method: 'PUT',
    headers: jsonHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(messageFromErrorBody(data, 'تعذر حفظ التعديلات'));
  }
  return mapBranchFromApi(data);
}

export async function deleteBranch(id) {
  const res = await fetch(`${API_BASE}/api/branches/${id}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', ...authHeaders() },
  });
  if (res.status === 204) return;
  const data = await res.json().catch(() => ({}));
  throw new Error(messageFromErrorBody(data, 'تعذر حذف الفرع'));
}

/** Multipart upload: appends files to the branch’s contracts (field name `files`). */
export async function uploadBranchAttachmentFiles(branchId, files) {
  const fd = new FormData();
  Array.from(files).forEach((file) => fd.append('files', file));
  const res = await fetch(`${API_BASE}/api/branches/${branchId}/attachments`, {
    method: 'POST',
    headers: { ...authHeaders() },
    body: fd,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(messageFromErrorBody(data, 'تعذر رفع الملفات'));
  }
  return mapBranchFromApi(data);
}

/** URL for GET download (call with authenticated fetch; Bearer required). */
export function branchAttachmentDownloadUrl(branchId, attachmentId) {
  return `${API_BASE}/api/branches/${branchId}/attachments/file/${encodeURIComponent(attachmentId)}`;
}
